// ponytail: IIFE guard prevents double-execution when script tag appears in both block and cart drawer
(function () {
if (window.__paypalPayLaterDefined) return;
window.__paypalPayLaterDefined = true;

let sdkLoadPromise = null;
// ponytail: singleton — createInstance registers paypal-message custom element; calling it twice throws
let sdkInstancePromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function ensureSdk(environment) {
  if (!sdkLoadPromise) {
    // ponytail: only load core; createInstance with components:['paypal-messages'] loads the messages module internally — loading it manually caused double-execution and a customElements.define conflict
    const host = environment === 'staging' ? 'www.sandbox.paypal.com' : 'www.paypal.com';
    sdkLoadPromise = loadScript(`https://${host}/web-sdk/v6/core`);
  }
  return sdkLoadPromise;
}

function ensureSdkInstance(clientId, environment) {
  if (!sdkInstancePromise) {
    sdkInstancePromise = ensureSdk(environment).then(() =>
      window.paypal.createInstance({
        clientId,
        pageType: 'product-details',
        components: ['paypal-messages'],
      })
    );
  }
  return sdkInstancePromise;
}

class PaypalPayLater extends HTMLElement {
  constructor() {
    super();
    this.content = null;
    this.learnMore = null;
    this.messageEl = null;
    this.variantChangeUnsubscriber = null;
    this.onProductSwap = this.onProductSwap.bind(this);
    this.onProductInfoChange = this.onProductInfoChange.bind(this);
  }

  async connectedCallback() {
    this.productInfo = this.closest('.product__info-container');

    try {
      const sdkInstance = await ensureSdkInstance(this.dataset.clientId, this.dataset.environment);

      const messagesInstance = sdkInstance.createPayPalMessages();
      this.messageEl = this.querySelector('paypal-message');

      this.content = await messagesInstance.fetchContent({
        amount: this.pricePounds(parseInt(this.dataset.pricePence, 10)),
        currencyCode: 'GBP',
        logoType: 'WORDMARK',
        textColor: 'MONOCHROME',
        onReady: (content) => this.messageEl.setContent(content),
      });

      this.learnMore = await messagesInstance.createLearnMore({
        presentationMode: 'MODAL',
      });
      this.learnMore.attachDefaultListener(this.messageEl);
    } catch (_) {
      this.classList.add('hidden');
      return;
    }

    this.subscribeToVariantChanges();
    this.bindDomListeners();
    this.recalculateFromDomWithRetry();
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
    if (this.learnMore && this.messageEl) this.learnMore.detachDefaultListener(this.messageEl);
    this.unbindDomListeners();
  }

  pricePounds(pricePence) {
    return (pricePence / 100).toFixed(2);
  }

  updateDisplay(pricePence) {
    const price = parseInt(pricePence, 10);
    const min = parseInt(this.dataset.minThreshold, 10);
    const max = parseInt(this.dataset.maxThreshold, 10);
    const inRange = price >= min && price <= max;

    this.classList.toggle('hidden', !inRange);

    if (inRange && this.content) {
      this.content.update({ amount: this.pricePounds(price) });
    }
  }

  subscribeToVariantChanges() {
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.data.sectionId === this.dataset.sectionId) {
        this.updateDisplay(event.data.variant?.price || 0);
      }
    });
  }

  onProductSwap() {
    this.recalculateFromDomWithRetry();
  }

  onProductInfoChange(event) {
    if (!event?.target?.classList?.contains('product-variant-id')) return;
    this.recalculateFromDomWithRetry();
  }

  bindDomListeners() {
    document.addEventListener('productLowerUpdated', this.onProductSwap);
    document.addEventListener('colorVariantChanged', this.onProductSwap);
    if (this.productInfo) {
      this.productInfo.addEventListener('change', this.onProductInfoChange);
    }
  }

  unbindDomListeners() {
    document.removeEventListener('productLowerUpdated', this.onProductSwap);
    document.removeEventListener('colorVariantChanged', this.onProductSwap);
    if (this.productInfo) {
      this.productInfo.removeEventListener('change', this.onProductInfoChange);
    }
  }

  recalculateFromDomWithRetry() {
    let attempts = 0;

    const run = () => {
      attempts += 1;
      const pricePence = this.getCurrentPricePenceFromDom();
      if (pricePence > 0) {
        this.updateDisplay(pricePence);
        return;
      }
      if (attempts < 6) setTimeout(run, 80);
    };

    run();
  }

  getCurrentPricePenceFromDom() {
    const productInfo = this.productInfo || this.closest('.product__info-container');
    if (!productInfo) return 0;

    const variantIdInput = productInfo.querySelector('.product-variant-id');
    const variantId = parseInt(
      variantIdInput?.value || this.dataset.productVariantId || '',
      10
    );

    if (variantId) {
      const variantDataScript = productInfo.querySelector(
        "variant-radios script[type='application/json']"
      );
      if (variantDataScript?.textContent) {
        try {
          const variants = JSON.parse(variantDataScript.textContent);
          const variant = variants.find((v) => v.id === variantId);
          if (variant?.price) return parseInt(variant.price, 10);
        } catch (_) {
          // fall through to wishlist fallback
        }
      }
    }

    const wishlistPrice = productInfo.querySelector('.product__wishlist[data-price]')?.dataset
      ?.price;
    if (wishlistPrice) {
      const val = parseFloat(wishlistPrice);
      if (!Number.isNaN(val)) return Math.round(val * 100);
    }

    return 0;
  }
}

if (!customElements.get('paypal-pay-later')) {
  customElements.define('paypal-pay-later', PaypalPayLater);
}

class PaypalCartMessages extends HTMLElement {
  async connectedCallback() {
    const price = parseInt(this.dataset.pricePence, 10);
    const min = parseInt(this.dataset.minThreshold, 10);
    const max = parseInt(this.dataset.maxThreshold, 10);
    if (price < min || price > max) return;

    try {
      const sdkInstance = await ensureSdkInstance(this.dataset.clientId, this.dataset.environment);
      const messagesInstance = sdkInstance.createPayPalMessages();
      const messageEl = this.querySelector('paypal-message');
      const content = await messagesInstance.fetchContent({
        amount: (price / 100).toFixed(2),
        currencyCode: 'GBP',
        logoType: 'WORDMARK',
        textColor: 'MONOCHROME',
        onReady: (c) => messageEl.setContent(c),
      });
      const learnMore = await messagesInstance.createLearnMore({ presentationMode: 'MODAL' });
      learnMore.attachDefaultListener(messageEl);
    } catch (_) {
      this.classList.add('hidden');
    }
  }
}

if (!customElements.get('paypal-cart-messages')) {
  customElements.define('paypal-cart-messages', PaypalCartMessages);
}
})();
